// F-2.0 Proposal Management — TypeScript models
// Mirrors OMS model.ProposalRevision and Order proposal fields (ADR-013 D5-D7)
// F2-VR (Valor Referência): reference_type + rev3_cents snapshot (ADR-PVR Decisions 1-8)

export type ProposalStatus =
  | "DRAFT"
  | "DRAFT_SENT"
  | "ACCEPTED"
  | "DECLINED"
  | "CHANGE_REQUESTED"
  | "EXPIRED"
  | "CANCELLED";

/** Reference type used to compute margin and validate minimum price.
 *  - REV3: reseller list price (Rev3) — default for new revisions
 *  - COST: catalog cost — admin-only, or fallback for legacy revisions
 *  - REV4: reseller Rev4 unit price snapshot — free reference (any role),
 *    falls back to REV3 then COST when rev4_cents is absent on an item.
 *  When persisted as null/undefined (legacy revisions), treated as COST.
 */
export type ProposalReferenceType = "REV3" | "COST" | "REV4";

/** Frontend-canonical role (pt-PT). Wire format is "ADMIN" | "COMMERCIAL" and
 *  must be translated via translateWireRole() at the API boundary. */
export type ProposalRole = "admin" | "comercial";

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  DRAFT: "Rascunho",
  DRAFT_SENT: "Enviada",
  ACCEPTED: "Aceite",
  DECLINED: "Recusada",
  CHANGE_REQUESTED: "Alterações Pedidas",
  EXPIRED: "Expirada",
  CANCELLED: "Cancelada",
};

export const PROPOSAL_STATUS_COLOR: Record<
  ProposalStatus,
  "default" | "primary" | "success" | "error" | "warning" | "info"
> = {
  DRAFT: "default",
  DRAFT_SENT: "primary",
  ACCEPTED: "success",
  DECLINED: "error",
  CHANGE_REQUESTED: "warning",
  EXPIRED: "default",
  CANCELLED: "error",
};

/** Snapshot item as stored in proposal_revisions.items_json */
export interface ProposalItem {
  product_id: string;
  name: string;
  reference?: string;
  /** Component type label from ERP (e.g. "Processador", "Placa Gráfica") */
  component_type?: string;
  /** Rev4 (reseller) unit price in cents at the time the item was added */
  rev4_cents?: number;
  /** Rev3 (reseller list) unit price in cents — snapshot at add-time.
   *  Used as reference value when revision.reference_type === "REV3".
   *  Optional for backwards compatibility with legacy items. */
  rev3_cents?: number;
  pvp_cents: number;
  cost_cents: number;
  quantity: number;
  snapshotted_at?: string;
}

/** Draft item while editing (before saving) */
export interface ProposalDraftItem extends ProposalItem {
  _localId: string; // UI-only key for React reconciliation
  /** Live stock quantity from catalog at the time the product was selected.
   *  Used to show a visual warning when a component has no available units.
   *  Not persisted in the revision snapshot — UI-only. */
  stock?: number;
}

/** GET /admin/orders/:id/proposal response */
export interface ProposalRevision {
  version: number;
  items: ProposalItem[];
  total_cents: number;
  notes: string;
  created_by: string;
  created_at: string;
  change_request_justification?: string;
  /** Reference type used when the revision was created.
   *  Absent/null means legacy revision (treat as COST). */
  reference_type?: ProposalReferenceType | null;
}

/** POST /admin/orders/:id/proposal request */
export interface CreateRevisionRequest {
  version: number; // optimistic lock — last known version (0 = first)
  items: ProposalItem[];
  total_cents: number;
  notes: string;
  /** Reference type chosen by the seller for this revision. */
  reference_type: ProposalReferenceType;
}

/** POST /admin/orders/:id/proposal/revise request */
export interface ReviseRequest {
  source_version: number;
}

/** PATCH /admin/orders/:id/proposal/respond request */
export interface RespondProposalRequest {
  action: "accept" | "decline";
  justification?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function calcTotal(items: ProposalDraftItem[]): number {
  return items.reduce((acc, i) => acc + i.pvp_cents * i.quantity, 0);
}

export function isEditable(status: ProposalStatus | undefined | null): boolean {
  return !status || status === "DRAFT" || status === "CHANGE_REQUESTED";
}

export function canSend(status: ProposalStatus | undefined | null): boolean {
  return status === "DRAFT";
}

export function canRevise(status: ProposalStatus | undefined | null): boolean {
  return status === "CHANGE_REQUESTED";
}

export function canRespond(status: ProposalStatus | undefined | null): boolean {
  return status === "DRAFT_SENT";
}

// ── F2-VR Reference helpers ─────────────────────────────────────────────────

/** Resolves the effective reference type for a revision. Legacy revisions
 *  (null/undefined reference_type) are treated as COST per ADR-PVR D3. */
export function effectiveReferenceType(
  type: ProposalReferenceType | null | undefined,
): ProposalReferenceType {
  return type ?? "COST";
}

/** True when the revision predates the F2-VR migration (no reference_type
 *  persisted). UI should render a "(legacy)" badge in that case. */
export function isLegacyReference(
  type: ProposalReferenceType | null | undefined,
): boolean {
  return type === null || type === undefined;
}

// ── PR 6a — Pricing v2 foundation ───────────────────────────────────────────
// Discriminated proposal response, drift, and resolve types aligned with the
// OMS Go backend (see oms-microservice/internal/service/proposal_drift.go and
// internal/handler/admin_proposal.go).
//
// Role narrowing convention (ADR PR 6a, "Convenções"):
// Always narrow `response.role` BEFORE destructuring `response.revision`. The
// discriminator is on `response`, not on `revision`; destructuring `revision`
// first loses the discriminator and TS falls back to the union of both shapes.

/** Shared item fields visible to both admin and comercial roles.
 *  Field names align with OMS `AdminItemDTO` / `ComercialItemDTO` wire (oms#97). */
interface BaseItemDTO {
  product_id: string;
  name: string;
  /** ERP/SKU reference (e.g. "i7-12700K"). Present on the wire via
   *  `reference,omitempty` on both item DTOs. */
  reference?: string;
  /** Component type label from ERP (e.g. "Processador", "Placa Gráfica").
   *  Non-sensitive — both roles see it. Optional because legacy snapshots
   *  predate ERP component-type enrichment. */
  component_type?: string;
  /** Pricing-key for the component type (e.g. "placa_grafica", "processador").
   *  Used to look up min-margin table. Different from `component_type` label. */
  component_type_key?: string;
  /** Rev3 unit price snapshot (cents). Always available post-F2-VR. */
  rev3_cents?: number;
  /** Rev4 unit price snapshot (cents). Optional — falls back to rev3 then cost. */
  rev4_cents?: number;
  /** Reseller PVP snapshot (cents). Legacy field — may be absent on revisions
   *  fetched via the new OMS discriminated DTO (oms#97). Use rev3_cents as
   *  the canonical "reference price" column instead. */
  pvp_cents?: number;
  /** Final unit price negotiated for this item in this revision. */
  unit_price_cents: number;
  /** Floor enforced server-side: MAX(rev4_or_fallback, cost × (1 + min_margin)). */
  min_price_cents: number;
  /** True when unit_price_cents < min_price_cents server-side. */
  margin_at_risk: boolean;
  /** Quantity ordered. Renamed from legacy `qty`. */
  quantity: number;
  /** VAT rate as a decimal fraction (e.g. 0.23 for 23%). Wire key: vat_rate_pct. */
  vat_rate_pct?: number;
  vat_regime?: string;
}

/** Admin-only item shape — includes cost_cents and margin_pct.
 *  Backend `GetLatestRevision` currently leaks these for comercial too
 *  (oms#91); the frontend API client strips them in defence-in-depth. */
export interface AdminItemDTO extends BaseItemDTO {
  cost_cents: number;
  /** Per-item gross margin pct (0..1). */
  margin_pct: number;
}

/** Comercial item shape — cost_cents and margin_pct are NEVER present.
 *  Compile-time enforcement: accessing `.cost_cents` on this type is a tsc
 *  error, even if backend leaks the field at runtime. */
export type ComercialItemDTO = BaseItemDTO;

/** Shared revision fields. Metadata fields (notes, created_by, created_at,
 *  change_request_justification) are non-sensitive and visible to both
 *  roles — they describe authorship and workflow state, not pricing. */
interface BaseRevisionDTO {
  version: number;
  /** Reference type for this revision. Absent/null on legacy pre-F2-VR
   *  revisions — consumers MUST resolve via `effectiveReferenceType()` which
   *  defaults legacy null → "COST". OMS Go wire emits `omitempty`. */
  reference_type?: ProposalReferenceType | null;
  total_cents: number;
  /** Internal notes attached to the revision. Empty string when absent on the
   *  wire (OMS emits `omitempty` so missing → undefined; the API client
   *  normalises to "" for stable rendering). */
  notes?: string;
  /** User identifier (email) that authored the revision. May be empty on
   *  legacy revisions migrated from pre-auth-tracking days. */
  created_by: string;
  /** ISO-8601 timestamp from the OMS wire. */
  created_at: string;
  /** Populated when the order moved to CHANGE_REQUESTED — the comercial's
   *  justification for requesting a new revision. Both roles see it. */
  change_request_justification?: string;
  /** Customer-facing preview link (frontstore /orcamento/<token>). Present
   *  when the proposal is DRAFT_SENT — re-issued by GET /proposal so staff
   *  can re-obtain it on reload. */
  preview_url?: string;
}

/** KPI block visible only to admins (oms#97). */
export interface AdminKPIs {
  above_rev3_pct: number;
  above_min_pct: number;
  real_gross_margin_pct: number;
}

/** KPI block visible to comercial (subset — no margin). */
export interface ComercialKPIs {
  above_rev3_pct: number;
  above_min_pct: number;
}

export interface AdminRevisionDTO extends BaseRevisionDTO {
  items: AdminItemDTO[];
  /** Revision-level KPIs. Present on new OMS wire (oms#97); absent on legacy. */
  kpis?: AdminKPIs;
}

export interface ComercialRevisionDTO extends BaseRevisionDTO {
  items: ComercialItemDTO[];
  kpis?: ComercialKPIs;
}

/** Discriminated proposal response. The `role` discriminator is JWT-derived
 *  via deriveProposalRole() at the API boundary, NOT taken from response.role
 *  (which the backend does not even emit on the wire). */
export type ProposalResponse =
  | { role: "admin"; revision: AdminRevisionDTO }
  | { role: "comercial"; revision: ComercialRevisionDTO };

// ── Drift types (consumed in PR 6b) ─────────────────────────────────────────
// Backend ref: oms-microservice/internal/service/proposal_drift.go
// Wire alignment notes:
//   - Severity has NO "none" — a proposal without drift has `items: []`.
//   - Field is one of "cost" | "rev3" | "name" — backend never emits "rev4"
//     nor "min_price".
//   - current_val and delta_pct are omitted for orphan/indeterminate.

export type DriftSeverity = "minor" | "major" | "orphan" | "indeterminate";

export type DriftField = "cost" | "rev3" | "name";

export interface DriftItem {
  compound_id: string;
  field: DriftField;
  snapshot_val: number | string;
  /** Omitted for orphan/indeterminate. */
  current_val?: number | string;
  /** Omitted for orphan/indeterminate/name. */
  delta_pct?: number;
  severity: DriftSeverity;
  /** True iff severity === "orphan". Mirrors backend convenience flag. */
  orphan?: boolean;
}

export interface DriftReport {
  /** Empty array means no drift detected. */
  items: DriftItem[];
}

// ── Resolve drift types (consumed in PR 6b) ─────────────────────────────────
// Backend ref: oms-microservice/internal/service/proposal_resolve.go.
// IMPORTANT: bulk-accept-all is a request HEADER (`X-Bulk-Accept-All: "true"`),
// NOT a body field. Justification is documented as required when MARGIN_AT_RISK
// but is NOT server-enforced — the frontend MUST gate it.

export type ResolveDriftStrategy =
  | "accept_current"
  | "keep_snapshot"
  | "create_new_revision";

export interface ResolveDriftRequest {
  strategy: ResolveDriftStrategy;
  /** Drifted product ids to resolve. Empty/omitted = all drifted items. */
  compound_ids?: string[];
  /** Frontend MUST set this when any resolved item is MARGIN_AT_RISK. */
  justification?: string;
}

export interface ResolveDriftResponse {
  strategy: ResolveDriftStrategy;
  resolved: number;
  /** Present iff strategy === "create_new_revision". */
  new_revision_id?: number;
}
